import { Import, Settings, User } from "lucide-react";
import { CanAccess, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { BldrOsMenuItems } from "@/components/bldros/navigation/BldrOsMenu";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";

type NavItem = {
  label: string;
  to: string;
  match: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", to: "/", match: "/" },
  { label: "Contacts", to: "/contacts", match: "/contacts/*" },
  { label: "Companies", to: "/companies", match: "/companies/*" },
  { label: "Deals", to: "/deals", match: "/deals/*" },

  // === BldrOS core modules ===
  { label: "Pipeline", to: "/pipeline", match: "/pipeline" },
  { label: "Estimates", to: "/estimates", match: "/estimates/*" },
  { label: "Jobs", to: "/jobs", match: "/jobs/*" },
  { label: "Invoices", to: "/invoices", match: "/invoices/*" },
];

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const pathname = location.pathname;

  let currentPath: string | false = false;
  if (matchPath("/", pathname)) {
    currentPath = "/";
  } else {
    const match = NAV_ITEMS.find(
      (item) => item.to !== "/" && matchPath(item.match, pathname),
    );
    currentPath = match ? match.to : false;
  }

  return (
    <nav className="grow">
      <header className="bg-secondary">
        <div className="px-4">
          <div className="flex justify-between items-center flex-1">
            {/* Brand */}
            <Link
              to="/"
              className="flex items-center gap-2 text-secondary-foreground no-underline"
              aria-label={`${title} home`}
            >
              <img className="[.light_&]:hidden h-6" src={darkModeLogo} alt={title} />
              <img className="[.dark_&]:hidden h-6" src={lightModeLogo} alt={title} />
              <h1 className="text-xl font-semibold">{title}</h1>
            </Link>

            {/* Primary nav */}
            <div>
              <nav className="flex">
                {NAV_ITEMS.map((item) => (
                  <NavigationTab
                    key={item.to}
                    label={item.label}
                    to={item.to}
                    isActive={currentPath === item.to}
                  />
                ))}
              </nav>
            </div>

            {/* Actions */}
            <div className="flex items-center">
              <ThemeModeToggle />
              <RefreshButton />
              <UserMenu>
                <ConfigurationMenu />
                <CanAccess resource="sales" action="list">
                  <UsersMenu />
                </CanAccess>
                <ImportFromJsonMenuItem />

                {/* === BldrOS: Contractor CRM menu === */}
                <BldrOsMenuItems />
              </UserMenu>
            </div>
          </div>
        </div>
      </header>
    </nav>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
      isActive
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`}
  >
    {label}
  </Link>
);

const UsersMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<UsersMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <User className="size-4" /> Users
      </Link>
    </DropdownMenuItem>
  );
};

const ConfigurationMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<ConfigurationMenu> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings className="size-4" />
        My info
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import className="size-4" /> Import data
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
